import { IAttribute, IAttribute2 } from '../components/DTStudio/interfaces'
import {
    IChartAPIPayloadDataSet,
    ISelectedAttributes,
} from '../components/ViewEntity/interfaces'
import {
    AttributesMetadata,
    AttributeType,
    BUSINESS_RULES_FIELD_ID,
    EntityStatus,
    ShipmentAttributes,
    UserPermissions,
} from '../constants/constants'
import { RootState } from '../redux/store'
import {
    fetchEntityInstanceData,
    fetchRecordsUIDsByTemplateId,
    fetchRulesetsForProduct,
} from '../services/entityInstanceServices'
import { fetchResolvedOrgDataOrgId } from '../services/orgServices'
import {
    isBoolean,
    isCountryISOcode,
    isCustomAttribute,
    isDateTime,
    isDecimalType,
    isEmail,
    isFile,
    isMultiSelect,
    isNumberType,
    isRadio,
    isSelect,
    isStringType,
    isTimeSeries,
} from './AttributeUtils'
import {
    getDateTimeInISOFormate,
    getDateTimeUserFormate,
    getDateYYYYMMDDFormate,
} from './DateAndTimeUtils'
import { getCountryCodes } from './GetCountryCodes'
import dayjs from 'dayjs'

export const updateAttributeValues = (
    attributeData: any[],
    thunkAPI: { getState: () => unknown },
    isEdit: boolean = false,
): IAttribute2[] => {
    const state = thunkAPI.getState() as RootState

    const permissions = state.org.permissions
    const userPreferTimezone =
        state.user.user?.preferences?.localization?.timeZone
    const isSuperAdmin = permissions?.includes(UserPermissions.ORG_ADMIN)

    return attributeData?.map((item: any) => {
        const commonProps = {
            hide:
                item?.metadata?.Hidden ||
                (item?.metadata?.Superviewer && !isSuperAdmin),

            disabled: !!(
                item?.metadata?.[AttributesMetadata.VIEW_ONLY] && !isSuperAdmin
            ),
        }

        if (isSelect(item) || isRadio(item) || isMultiSelect(item)) {
            let listValues: any[] = []

            try {
                listValues = JSON.parse(item.range?.trim() || '[]')
            } catch {
                listValues = item.range?.split(',') || []
            }

            const updatedList = listValues
                ?.map((i: any) => {
                    if (i?.key !== undefined && i?.value !== undefined) {
                        return { id: i.key, name: i.value }
                    }
                    if (typeof i === 'object') {
                        return {
                            id: JSON.stringify(i),
                            name: JSON.stringify(i),
                        }
                    }
                    return { id: i, name: i }
                })
                ?.filter((i: any) => i.id || i.name)

            return {
                ...item,
                ...commonProps,
                list: updatedList,
                value: isMultiSelect(item)
                    ? item.value ?? []
                    : item.value ?? '',
            }
        }

        if (isBoolean(item)) {
            return {
                ...item,
                ...commonProps,
                value:
                    typeof item.value === 'boolean'
                        ? item.value
                        : item.defaultValue?.toLowerCase() === 'true',
            }
        }

        if (isDateTime(item)) {
            let updatedValue = item.value

            if (updatedValue?.datetime) {
                const timezone =
                    updatedValue.timezone ||
                    userPreferTimezone ||
                    dayjs.tz.guess()

                updatedValue = {
                    datetime: getDateTimeUserFormate(
                        updatedValue.datetime,
                        'MM/DD/YYYY HH:mm',
                        timezone,
                    ),
                    timezone,
                }
            }

            return {
                ...item,
                ...commonProps,
                value: updatedValue,
            }
        }

        if (isFile(item)) {
            let parsedValue = ''

            try {
                parsedValue = JSON.parse(item.value)
            } catch {
                parsedValue = item.value
            }

            return {
                ...item,
                ...commonProps,
                value: parsedValue,
            }
        }

        if (isEmail(item)) {
            const isArray = item.metadata?.Array

            const rawValue = item.value

            const normalizeArrayValue = () => {
                if (Array.isArray(rawValue)) return rawValue
                if (rawValue) return [rawValue]
                return []
            }

            const normalizeSingleValue = () => {
                if (Array.isArray(rawValue)) return rawValue[0]
                return rawValue ?? ''
            }

            const value = isArray
                ? normalizeArrayValue()
                : normalizeSingleValue()

            return {
                ...item,
                ...commonProps,
                value,
            }
        }

        if (isCustomAttribute(item)) {
            return {
                ...item,
                ...commonProps,
                hide: true,
            }
        }

        if (isCountryISOcode(item)) {
            return {
                ...item,
                ...commonProps,
                list: getCountryCodes(),
            }
        }
        if (isStringType(item) || isNumberType(item) || isDecimalType(item)) {
            return {
                ...item,
                ...commonProps,
                value: isEdit ? item.value ?? '' : item.defaultValue ?? '',
            }
        }

        return {
            ...item,
            ...commonProps,
            value: item.value ?? '',
        }
    })
}

export const updateValuesForPayload = (
    attributes: unknown[] | undefined,
): (IAttribute2 & { value: unknown })[] => {
    const updatedFields = (attributes || []).map((i: any) => {
        if (
            i.id.toLowerCase() === BUSINESS_RULES_FIELD_ID ||
            i.dataType.toLowerCase() === AttributeType.RULESET
        ) {
            let updatedValue = null
            if (Array.isArray(i.value)) {
                updatedValue = i.value?.map((i: any) => i.id)
            }
            return { ...i, value: updatedValue?.length ? updatedValue : null }
        } else if (i.dataType.toLowerCase() === AttributeType.DEVICE_RELATION) {
            const exsitingItems = new Map(
                i.selectedEntities?.map((e: any) => [e.entityId, e]),
            )
            const updatedValue = i.value
                ?.filter((d: any) => {
                    //if it's exsiting item. need to stop to send in API payload.
                    //if even exsting one and status is diffrent need to send to API
                    const exsitingItem: any = exsitingItems.get(d.entityId)
                    return !exsitingItem || exsitingItem.status !== d.status
                })
                ?.map((c: any) => {
                    return {
                        instanceId: c.entityId,
                        status: c.status || 'ACTIVE',
                    }
                })
            return { ...i, value: updatedValue }
        } else if (i.dataType.toLowerCase() === AttributeType.MULTI_SELECT) {
            return { ...i, value: i.value?.length ? i.value : null }
        } else if (i.dataType.toLowerCase() === AttributeType.DATE_TIME) {
            let updatedValue = null
            if (i.value?.datetime) {
                updatedValue = {
                    ...i.value,
                    datetime: getDateTimeInISOFormate(
                        i.value?.datetime,
                        i.value?.timezone,
                        'MM/DD/YYYY HH:mm',
                    ),
                }
            }
            return { ...i, value: updatedValue }
        } else if (i.dataType.toLowerCase() === AttributeType.DATE) {
            let updatedValue = null
            if (i.value !== '' && i.value !== null) {
                updatedValue = getDateYYYYMMDDFormate(i.value)
            }
            return { ...i, value: updatedValue }
        } else if (i.dataType.toLowerCase() === AttributeType.FILE) {
            let updatedValue = null
            if (i.value !== '' && i.value !== null) {
                updatedValue = JSON.stringify(i.value)
            }
            return { ...i, value: updatedValue }
        } else if (
            i.dataType.toLowerCase() === AttributeType.NUMBER ||
            i.dataType.toLowerCase() === AttributeType.DECIMAL ||
            i.dataType.toLowerCase() === AttributeType.THRESHOLD
        ) {
            return {
                ...i,
                value:
                    i.value !== null && i.value !== undefined && i.value !== ''
                        ? Number(i.value)
                        : null,
            }
        } else if (
            i.dataType.toLowerCase() === AttributeType.TEMPERATURE ||
            i.dataType.toLowerCase() === AttributeType.HUMIDITY ||
            i.dataType.toLowerCase() === AttributeType.WEIGHT ||
            i.dataType.toLowerCase() === AttributeType.DISTANCE ||
            i.dataType.toLowerCase() === AttributeType.CURRENCY
        ) {
            let updatedVlaue = null
            if (i.value !== null && i.value !== undefined) {
                updatedVlaue = {
                    value: i.value !== '' ? Number(i.value) : null,
                    units: i.unit ?? '',
                }
            }
            return { ...i, value: updatedVlaue }
        } else if (i.dataType.toLowerCase() === AttributeType.SEALINE) {
            return { ...i, value: i.value ? i.value.id : null }
        } else if (i.dataType.toLowerCase() === AttributeType.JSON) {
            const updatedValue = transformJSONValueForUpdateAPIPayload(i)
            return { ...i, value: normalizeValue(updatedValue) }
        } else {
            return {
                ...i,
                value: normalizeValue(i.value),
            }
        }
    })
    return updatedFields
}

export const transformJSONValueForUpdateAPIPayload = (
    attribute: IAttribute,
): any => {
    let updatedValue = attribute.value
    if (Array.isArray(attribute.value)) {
        updatedValue = attribute.value.map((item: any) => {
            const updatedItem: any = {}

            for (const key in item) {
                const val = item[key]
                // Check if nested value is of datatype Entity
                if (
                    val &&
                    typeof val === 'object' &&
                    val.dataType &&
                    val.dataType?.toLowerCase() === AttributeType.ENTITY
                ) {
                    updatedItem[key] = val.entityId
                } else {
                    updatedItem[key] = val
                }
            }

            return updatedItem
        })
    } else if (
        typeof attribute.value === 'object' &&
        attribute.value !== null
    ) {
        const updatedObj: any = {}

        for (const key in attribute.value) {
            const val = attribute.value[key]

            if (
                val &&
                typeof val === 'object' &&
                val.dataType &&
                val.dataType.toLowerCase() === AttributeType.ENTITY
            ) {
                updatedObj[key] = val.entityId
            } else {
                updatedObj[key] = val
            }
        }

        updatedValue = updatedObj
    }
    return updatedValue
}

export const isValidEmail = (emailAddress: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(emailAddress.toLowerCase())
}

export const getChartAPIPayloadDataSets = (
    selectedAttributes: ISelectedAttributes[],
    filterOutNonTimeSeriesData = true,
): IChartAPIPayloadDataSet[] => {
    return selectedAttributes
        .filter((item) => {
            if (filterOutNonTimeSeriesData) {
                return isTimeSeries(item.attribute)
            } else {
                return true
            }
        })
        .map((item) => {
            return {
                entityInstanceId: item.entityInstanceId,
                attributeId: item.attribute.id,
                dataType: item.attribute.dataType,
            }
        })
}

export const getAttributeForChartDataSet = (
    selectedAttributes: ISelectedAttributes[],
    attributeId: string,
    entityInstanceId: string,
): IAttribute | undefined => {
    return selectedAttributes.find(
        (item) =>
            item.attribute.id === attributeId &&
            item.entityInstanceId === entityInstanceId,
    )?.attribute
}

export const normalizeValue = (value: unknown): any => {
    if (
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' &&
            value !== null &&
            Object.keys(value).length === 0)
    ) {
        return null
    }
    return value
}
/**
 * A reference to another entity, in the `{entityId, templateId, uid}` form the
 * create form holds it in.
 */
export const isEntityReferenceValue = (
    value: unknown,
): value is { entityId: string; templateId: string; uid?: string } =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !!(value as { entityId?: unknown }).entityId &&
    !!(value as { templateId?: unknown }).templateId

/**
 * Sets the form's starting values from a map of attribute id to value. This is
 * the one place values get applied, whether they come from a saved preset
 * template or from the org's legacy defaults. Whatever the source, it:
 * - re-validates each entity reference and nulls it when the target is gone,
 *   refreshing the display uid otherwise;
 * - derives the Share List from the receiver when the map doesn't set one;
 * - loads the product's rulesets, exactly as picking a product by hand does.
 *   Skipping that would create shipments with no alarms, silently.
 * A field the map doesn't mention is left as it is.
 */
/**
 * Whether an entity reference can be used in an org: it loads there (it has a
 * uid) and is not deleted.
 */
const isReferenceAvailable = (
    response: { uid?: string; status?: unknown } | undefined,
): boolean => !!response?.uid && response.status !== EntityStatus.DELETE

/**
 * Whether the org's own dropdown for that kind of entity lists the reference.
 * Loading a record by id works across orgs, so it can't tell whether a Program
 * shares it (a Shipper of another Program loads fine but is not offered).
 * Asks the same list the dropdown reads; a list that can't be read counts as
 * listed, so a failed call never wipes what the user chose.
 */
const isReferenceListedInOrg = async (
    orgId: string,
    reference: { entityId: string; templateId: string; uid?: string },
): Promise<boolean> => {
    const records = await fetchRecordsUIDsByTemplateId(
        orgId,
        reference.templateId,
        {
            pageNumber: 1,
            pageSize: 30,
            searchText: reference.uid ?? '',
            filterBy: EntityStatus.ACTIVE,
            includeUseOnly: true,
            includeCommonOrg: true,
        },
    )
    const list = records?.data
    return (
        !Array.isArray(list) ||
        list.some(
            (record) =>
                (record as { entityId?: string }).entityId ===
                reference.entityId,
        )
    )
}

const applyDefaultValueMap = async (
    attributes: IAttribute[],
    orgId: string,
    values: Record<string, unknown>,
): Promise<{
    updatedAttributes: IAttribute[]
    productRulesets: { id: string; name: string }[]
    unavailableAttributeIds: string[]
}> => {
    const resolved: Record<string, unknown> = {}
    Object.entries(values).forEach(([id, value]) => {
        if (attributes?.some((attr) => attr.id === id)) {
            resolved[id] = value
        }
    })

    const validationResults = await Promise.all(
        Object.entries(resolved)
            .filter(([, value]) => isEntityReferenceValue(value))
            .map(([id, value]) => {
                const reference = value as {
                    entityId: string
                    templateId: string
                    uid?: string
                }
                return Promise.all([
                    fetchEntityInstanceData(
                        orgId,
                        reference.templateId,
                        reference.entityId,
                    ),
                    isReferenceListedInOrg(orgId, reference),
                ]).then(([response, listed]) => ({ id, response, listed }))
            }),
    )

    // Share List carried by the resolved receiver. Mirrors the manual
    // dropdown-selection path in EntityTypeAttribute, which auto-populates the
    // shipment's Share List from the selected receiver's shareList attribute.
    let receiverShareList: unknown[] | null = null
    const unavailableAttributeIds: string[] = []

    validationResults.forEach(({ id, response, listed }) => {
        const reference = resolved[id] as { uid?: string }
        if (!listed || !isReferenceAvailable(response)) {
            resolved[id] = null
            unavailableAttributeIds.push(id)
            return
        }
        resolved[id] = { ...reference, uid: response?.uid || reference.uid }
        if (id === ShipmentAttributes.DestinationLocationAttributeId) {
            const shareList = response?.attributes?.find(
                (a: IAttribute) =>
                    a.id === ShipmentAttributes.ShareListAttributeId,
            )?.value
            if (Array.isArray(shareList) && shareList.length > 0) {
                receiverShareList = shareList
            }
        }
    })

    const updatedAttributes = attributes?.map((attr) => {
        if (Object.prototype.hasOwnProperty.call(resolved, attr.id)) {
            return { ...attr, value: resolved[attr.id] }
        }
        // Auto-populate the shipment's Share List from the receiver (only when
        // the receiver was resolved and carries a non-empty list).
        if (
            attr.id === ShipmentAttributes.ShareListAttributeId &&
            resolved[ShipmentAttributes.DestinationLocationAttributeId] &&
            receiverShareList
        ) {
            return { ...attr, value: receiverShareList }
        }
        return attr
    })

    let productRulesets: { id: string; name: string }[] = []
    const productEntityId = (
        resolved[ShipmentAttributes.ProductAttributeId] as {
            entityId?: string
        } | null
    )?.entityId
    if (productEntityId) {
        productRulesets = await fetchRulesetsForProduct(orgId, productEntityId)
    }

    return { updatedAttributes, productRulesets, unavailableAttributeIds }
}

/**
 * Fills a new entity's form. With `presetValues` (a saved preset template's
 * map, already in form format) that map is the only source. Without it the
 * org's legacy defaults - defaultProduct, defaultShipper, defaultReceiver -
 * apply unchanged. The two never blend: a field a preset template leaves blank
 * stays blank.
 */
export const setDefaultValues = async (
    attributes: IAttribute[],
    orgId: string,
    presetValues?: Record<string, unknown>,
): Promise<{
    updatedAttributes: IAttribute[]
    productRulesets: { id: string; name: string }[]
    unavailableAttributeIds?: string[]
}> => {
    if (presetValues) {
        return applyDefaultValueMap(attributes, orgId, presetValues)
    }

    const hasProductAttribute = attributes?.find(
        (i) => i.id === ShipmentAttributes.ProductAttributeId,
    )
    const hasOriginAttribute = attributes?.find(
        (i) => i.id === ShipmentAttributes.OriginLocationAttributeId,
    )
    const hasDestinationAttribute = attributes?.find(
        (i) => i.id === ShipmentAttributes.DestinationLocationAttributeId,
    )

    const entityType: string[] = []
    if (hasProductAttribute) {
        entityType.push(ShipmentAttributes.ProductAttributeId)
    }
    if (hasOriginAttribute) {
        entityType.push(ShipmentAttributes.OriginLocationAttributeId)
    }
    if (hasDestinationAttribute) {
        entityType.push(ShipmentAttributes.DestinationLocationAttributeId)
    }

    if (entityType.length === 0) {
        return { updatedAttributes: attributes, productRulesets: [] }
    }

    const selectedOrgData = await fetchResolvedOrgDataOrgId(
        orgId,
        entityType.join(','),
        'entityType',
    )
    const legacyValues: Record<string, unknown> = {}
    if (hasProductAttribute) {
        legacyValues[ShipmentAttributes.ProductAttributeId] =
            selectedOrgData?.preferences?.defaultProduct || null
    }
    if (hasOriginAttribute) {
        legacyValues[ShipmentAttributes.OriginLocationAttributeId] =
            selectedOrgData?.preferences?.defaultShipper || null
    }
    if (hasDestinationAttribute) {
        legacyValues[ShipmentAttributes.DestinationLocationAttributeId] =
            selectedOrgData?.preferences?.defaultReceiver || null
    }

    return applyDefaultValueMap(attributes, orgId, legacyValues)
}

/**
 * Converts a saved preset template's values (the create API's payload format)
 * into the values the form holds. Non-reference values go through
 * `updateAttributeValues` in edit mode, the same conversion a saved record gets
 * when it is opened, so dates, files, lists and the rest come out exactly as an
 * edit form would show them. Entity references are already in form format.
 * Values for attributes the preset template can't hold, or the form doesn't
 * have, are dropped.
 */
export const presetValuesToFormValues = (
    rawAttributes: any[],
    presetValues: Record<string, unknown>,
    thunkAPI: { getState: () => unknown },
    isTemplatable: (attribute: any) => boolean,
): Record<string, unknown> => {
    const allowed = (rawAttributes ?? []).filter(
        (attribute) =>
            Object.prototype.hasOwnProperty.call(presetValues, attribute.id) &&
            isTemplatable(attribute),
    )
    const formValues: Record<string, unknown> = {}
    const plain: any[] = []
    allowed.forEach((attribute) => {
        const value = presetValues[attribute.id]
        if (isEntityReferenceValue(value)) {
            formValues[attribute.id] = value
        } else {
            plain.push({ ...attribute, value })
        }
    })
    updateAttributeValues(plain, thunkAPI, true).forEach((attribute) => {
        formValues[attribute.id] = attribute.value
    })
    return formValues
}

/**
 * After the Program or Type changes, the references the user already chose
 * (receiver, product, locations) stay only if they are available in the new
 * org; the others are cleared. Also returns the business rules of the kept
 * product, since those are loaded per org.
 */
export const keepAvailableReferences = async (
    attributes: IAttribute[],
    orgId: string,
): Promise<{
    attributes: IAttribute[]
    unavailableAttributeIds: string[]
    productRulesets: { id: string; name: string }[]
}> => {
    /* A multi-select holds a list of references; each one is checked and the
       unavailable ones are dropped. */
    const referencesOf = (value: unknown): unknown[] =>
        (Array.isArray(value) ? value : [value]).filter(isEntityReferenceValue)
    const availability = new Map<string, boolean>()
    const keyOf = (reference: { entityId: string; templateId: string }) =>
        `${reference.templateId}:${reference.entityId}`
    await Promise.all(
        attributes
            .flatMap((attribute) => referencesOf(attribute.value))
            .map((reference) => {
                const { entityId, templateId, uid } = reference as {
                    entityId: string
                    templateId: string
                    uid?: string
                }
                return Promise.all([
                    fetchEntityInstanceData(orgId, templateId, entityId),
                    isReferenceListedInOrg(orgId, {
                        entityId,
                        templateId,
                        uid,
                    }),
                ]).then(([response, listed]) => {
                    availability.set(
                        keyOf({ entityId, templateId }),
                        listed && isReferenceAvailable(response),
                    )
                })
            }),
    )
    const isKept = (reference: unknown): boolean =>
        availability.get(
            keyOf(reference as { entityId: string; templateId: string }),
        ) !== false
    const unavailableAttributeIds: string[] = []
    const kept = attributes.map((attribute) => {
        const references = referencesOf(attribute.value)
        if (references.every(isKept)) {
            return attribute
        }
        unavailableAttributeIds.push(attribute.id)
        return {
            ...attribute,
            value: Array.isArray(attribute.value)
                ? attribute.value.filter(
                      (item) => !isEntityReferenceValue(item) || isKept(item),
                  )
                : null,
        }
    })
    const productId = (
        kept.find((a) => a.id === ShipmentAttributes.ProductAttributeId)
            ?.value as { entityId?: string } | null
    )?.entityId
    const productRulesets = productId
        ? await fetchRulesetsForProduct(orgId, productId)
        : []
    return { attributes: kept, unavailableAttributeIds, productRulesets }
}
